import type { WeightUnit } from '@repo/schema/load'

/** The unit a weight is shown in when the load has none recorded. */
export const DEFAULT_WEIGHT_UNIT: WeightUnit = 't'

/**
 * Weight is stored as a whole number of kilograms, so tonnes carry at most three
 * decimals (0.001 t = 1 kg) and kilograms carry none. Both patterns admit the
 * in-progress strings a user types on the way to a value — an empty field, a
 * trailing `.`, a leading `.` — which a plain number parse would destroy.
 */
const DISPLAY_PATTERN = {
  kg: /^\d*$/,
  t: /^\d*(\.\d{0,3})?$/,
} satisfies Record<WeightUnit, RegExp>

/** Returns the value to display, or `null` when the keystroke should be rejected. */
export const sanitizeWeightInput = (value: string, unit: WeightUnit): null | string =>
  DISPLAY_PATTERN[unit].test(value) ? value : null

/**
 * `Number('')` is 0 rather than NaN, so an empty field has to be caught before
 * parsing or clearing the input would read as a weight of zero.
 */
export const toKg = (displayValue: string, unit: WeightUnit): number | undefined => {
  if (displayValue === '' || displayValue === '.') return undefined

  const parsed = Number(displayValue)
  if (!Number.isFinite(parsed)) return undefined

  return unit === 't' ? Math.round(parsed * 1000) : Math.round(parsed)
}

export const toDisplayValue = (kg: null | number | undefined, unit: WeightUnit): string => {
  if (kg === null || kg === undefined) return ''
  if (unit === 'kg') return String(Math.round(kg))

  return String(Number((kg / 1000).toFixed(3)))
}
